// Prototype-only mock data and types for the IH-6912 Sharing & Access spike.
// Nothing here is wired to real APIs. This file lives under _prototype to make it
// obvious that it's throwaway and not for reuse.

import dayjs from 'dayjs';

export type IAccessLevel = 'viewer' | 'co-owner';

export interface IPrototypePerson {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
    access: IAccessLevel;
    status: 'active' | 'pending';
    onCareMap: boolean;
    careMapRole?: string;
    /** ISO date. For active people this is their joined-at; for pending it's invited-at. */
    addedAt?: string;
}

export type IHivePreset = 'brand-new' | 'populated' | 'full';

// Shape consumed by the unified PeopleList renderer. The mock-data and
// real-data sources both adapt to this so the row UI is consistent.
export type IRoleDisplay = 'Owner' | 'Co-Owner' | 'Viewer';

export interface IPersonRow {
    key: string;
    name: string;
    email: string;
    initials: string;
    role: IRoleDisplay;
    isOwner: boolean;
    isPending: boolean;
    statusLine: string;
    careMapRole?: string;
    onRemove?: () => Promise<void>;
    /** When set, the row menu shows "Update in Care Map" — only meaningful for people on the care map. */
    onUpdateInCareMap?: () => void;
}

// Relationships from SupportGroupDataProvider that don't represent family —
// used to pick the right Edit page title (Community Support vs Inner Circle Member).
const COMMUNITY_RELATIONSHIPS = new Set<string>([
    'Professional Caregiver',
    'Friend',
    'Housemate',
    'Roommate',
    'Neighbor'
]);

export const isFamilyRelationship = (label?: string): boolean => {
    if (!label) {
        return false;
    }
    return !COMMUNITY_RELATIONSHIPS.has(label);
};

// The brand-new preset is the default so the prototype shows what a first-time
// inviter sees: zero co-owners used, empty list. The populated preset is for
// comparing to a hive that already has people in it.
export const emptyMockPeople: IPrototypePerson[] = [];

// Mirrors the doc's example hive — Sarah and James as co-owners, Robin as viewer,
// plus one pending invite to make the "invite sent" state visible.
export const populatedMockPeople: IPrototypePerson[] = [
    {
        id: 'p1',
        firstName: 'Sarah',
        lastName: 'Williams',
        email: 'sarah@example.com',
        access: 'co-owner',
        status: 'active',
        onCareMap: true,
        careMapRole: 'Daughter',
        addedAt: '2025-09-15T15:24:00Z'
    },
    {
        id: 'p2',
        firstName: 'James',
        lastName: 'Whitfield Jr.',
        email: 'jamesjr@example.com',
        access: 'co-owner',
        status: 'active',
        onCareMap: true,
        careMapRole: 'Son',
        addedAt: '2025-10-02T12:00:00Z'
    },
    {
        id: 'p3',
        firstName: 'Robin',
        lastName: 'Sato',
        email: 'robin@example.com',
        access: 'viewer',
        status: 'active',
        onCareMap: false,
        addedAt: '2026-01-10T18:30:00Z'
    },
    {
        id: 'p4',
        firstName: 'Marcus',
        lastName: 'Lee',
        email: 'marcus@example.com',
        access: 'viewer',
        status: 'pending',
        onCareMap: false,
        addedAt: '2026-05-20T09:15:00Z'
    }
];

// A larger mock hive — lots of people in various states. Used to demonstrate
// what the list feels like when it's busy: at the co-owner quota, multiple
// active viewers with mixed care-map presence, multiple pending invites.
export const fullMockPeople: IPrototypePerson[] = [
    {
        id: 'f1',
        firstName: 'Sarah',
        lastName: 'Williams',
        email: 'sarah.williams@example.com',
        access: 'co-owner',
        status: 'active',
        onCareMap: true,
        careMapRole: 'Daughter',
        addedAt: '2025-09-15T15:24:00Z'
    },
    {
        id: 'f2',
        firstName: 'James',
        lastName: 'Whitfield Jr.',
        email: 'jamesjr@example.com',
        access: 'co-owner',
        status: 'active',
        onCareMap: true,
        careMapRole: 'Son',
        addedAt: '2025-10-02T12:00:00Z'
    },
    {
        id: 'f3',
        firstName: 'Robin',
        lastName: 'Sato',
        email: 'robin.sato@example.com',
        access: 'viewer',
        status: 'active',
        onCareMap: true,
        careMapRole: 'Neighbor',
        addedAt: '2026-01-10T18:30:00Z'
    },
    {
        id: 'f4',
        firstName: 'Maria',
        lastName: 'Lopez',
        email: 'maria@example.com',
        access: 'viewer',
        status: 'active',
        onCareMap: false,
        addedAt: '2026-02-22T11:00:00Z'
    },
    {
        id: 'f5',
        firstName: 'Tom',
        lastName: 'Chen',
        email: 'tom.chen@example.com',
        access: 'viewer',
        status: 'active',
        onCareMap: false,
        addedAt: '2026-03-14T16:45:00Z'
    },
    {
        id: 'f6',
        firstName: 'Emily',
        lastName: 'Park',
        email: 'emily.park@example.com',
        access: 'viewer',
        status: 'active',
        onCareMap: true,
        careMapRole: 'Niece',
        addedAt: '2026-04-08T10:20:00Z'
    },
    {
        id: 'f7',
        firstName: 'Marcus',
        lastName: 'Lee',
        email: 'marcus@example.com',
        access: 'viewer',
        status: 'pending',
        onCareMap: false,
        addedAt: '2026-05-20T09:15:00Z'
    },
    {
        id: 'f8',
        firstName: 'Linda',
        lastName: 'Park',
        email: 'linda.park@example.com',
        access: 'viewer',
        status: 'pending',
        onCareMap: true,
        careMapRole: 'Aunt',
        addedAt: '2026-05-22T14:00:00Z'
    },
    {
        id: 'f9',
        firstName: 'David',
        lastName: 'Kim',
        email: 'david@example.com',
        access: 'viewer',
        status: 'pending',
        onCareMap: false,
        addedAt: '2026-05-25T08:30:00Z'
    }
];

export const getPresetPeople = (preset: IHivePreset): IPrototypePerson[] => {
    if (preset === 'populated') {
        return populatedMockPeople;
    }
    if (preset === 'full') {
        return fullMockPeople;
    }
    return emptyMockPeople;
};

// Paywall reality from the plan: 2 free co-owners, then paid.
export const FREE_CO_OWNER_QUOTA = 2;

export const countCoOwners = (people: IPrototypePerson[]): number => {
    return people.filter(p => p.access === 'co-owner').length;
};

export const getInitials = (firstName: string, lastName: string): string => {
    const f = firstName.trim().charAt(0).toUpperCase();
    const l = lastName.trim().charAt(0).toUpperCase();
    return `${f}${l}` || '?';
};

const formatDate = (iso?: string): string => {
    if (!iso) {
        return 'today';
    }
    return dayjs(iso).format('M/D/YY');
};

// Adapter: mock person → unified row. Optional remove handler comes from the
// host so the mock-data list can wire up a local delete from its own state.
export const mockPersonToRow = (
    person: IPrototypePerson,
    onRemove?: () => Promise<void>
): IPersonRow => {
    const isPending = person.status === 'pending';
    const dateStr = formatDate(person.addedAt);
    return {
        key: person.id,
        name: `${person.firstName} ${person.lastName}`.trim(),
        email: person.email,
        initials: getInitials(person.firstName, person.lastName),
        role: person.access === 'co-owner' ? 'Co-Owner' : 'Viewer',
        isOwner: false,
        isPending,
        statusLine: isPending ? `Invited ${dateStr}` : `Joined ${dateStr}`,
        careMapRole: person.onCareMap ? person.careMapRole : undefined,
        onRemove
    };
};
